import { useState } from 'react';
import { useEditor } from '../../state/store';
import { ColorMode, colorToHex, hexToColor } from '../../core/types';
import { Field, Modal, NumberInput, Segmented } from '../widgets/common';

const PRESETS: ReadonlyArray<{ label: string; width: number; height: number; note: string }> = [
  { label: '80×24', width: 80, height: 24, note: 'Classic terminal' },
  { label: '80×25', width: 80, height: 25, note: 'DOS / IRC' },
  { label: '120×30', width: 120, height: 30, note: 'PuTTY default-ish' },
  { label: '120×40', width: 120, height: 40, note: 'Modern SSH' },
  { label: '160×50', width: 160, height: 50, note: 'Wide dashboard' },
  { label: '200×60', width: 200, height: 60, note: 'Large canvas' },
];

export function NewDocumentDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [name, setName] = useState('Untitled');
  const [width, setWidth] = useState(100);
  const [height, setHeight] = useState(30);
  const [colorMode, setColorMode] = useState<ColorMode>('truecolor');
  const [background, setBackground] = useState('#06070a');

  const create = () => {
    useEditor.getState().newDocument({
      name,
      width,
      height,
      colorMode,
      background: hexToColor(background),
    });
    onClose();
  };

  return (
    <Modal
      title="New document"
      onClose={onClose}
      width={520}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={create}>
            Create
          </button>
        </>
      }
    >
      <Field label="Name">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <div className="faint" style={{ margin: '10px 0 6px' }}>
        Presets
      </div>
      <div className="grid3" style={{ marginBottom: 12 }}>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            className={`btn${width === p.width && height === p.height ? ' active' : ''}`}
            style={{ flexDirection: 'column', height: 44, gap: 1 }}
            onClick={() => {
              setWidth(p.width);
              setHeight(p.height);
            }}
          >
            <span className="mono">{p.label}</span>
            <span className="faint" style={{ fontSize: 10 }}>
              {p.note}
            </span>
          </button>
        ))}
      </div>

      <Field label="Width">
        <NumberInput value={width} min={1} max={2000} onChange={setWidth} />
      </Field>
      <Field label="Height">
        <NumberInput value={height} min={1} max={2000} onChange={setHeight} />
      </Field>
      <Field label="Colour mode">
        <Segmented
          value={colorMode}
          options={[
            { value: 'ansi16' as ColorMode, label: 'ANSI 16' },
            { value: 'ansi256' as ColorMode, label: 'ANSI 256' },
            { value: 'truecolor' as ColorMode, label: 'TrueColor' },
          ]}
          onChange={setColorMode}
        />
      </Field>
      <Field label="Background">
        <div className="row">
          <input
            type="color"
            value={background}
            onChange={(e) => setBackground(e.target.value)}
            style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--border)', borderRadius: 4 }}
          />
          <input className="input mono" value={background} onChange={(e) => setBackground(e.target.value)} />
        </div>
      </Field>
      <p className="faint" style={{ lineHeight: 1.5 }}>
        {width * height} cells. Colour mode only affects export and preview — the document always stores
        full RGB, so you can switch modes later without losing colour.
      </p>
      <span style={{ display: 'none' }}>{colorToHex(0)}</span>
    </Modal>
  );
}
